/**
 * Utility functions for account drilldown and overview feature
 */

import type { TransactionWithRelations as Transaction } from "@/integrations/supabase/transaction.models";
import type { TransactionFilters, TimePeriod } from "@/types/account-drilldown";

/**
 * Sort transactions by date in descending order (most recent first)
 */
export function sortTransactionsByDate(
  transactions: Transaction[],
): Transaction[] {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.transaction_date).getTime();
    const dateB = new Date(b.transaction_date).getTime();
    return dateB - dateA;
  });
}

/**
 * Filter transactions by date range
 */
export function filterTransactionsByDateRange(
  transactions: Transaction[],
  startDate: string,
  endDate: string,
): Transaction[] {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();

  return transactions.filter((transaction) => {
    const transactionDate = new Date(transaction.transaction_date).getTime();
    return transactionDate >= start && transactionDate <= end;
  });
}

/**
 * Filter transactions by category
 */
export function filterTransactionsByCategory(
  transactions: Transaction[],
  categoryId: string,
): Transaction[] {
  return transactions.filter(
    (transaction) => transaction.category_id === categoryId,
  );
}

/**
 * Apply all filters to transactions
 */
export function applyTransactionFilters(
  transactions: Transaction[],
  filters: TransactionFilters,
): Transaction[] {
  let filtered = [...transactions];

  // Apply date range filter
  if (filters.dateFrom && filters.dateTo) {
    filtered = filterTransactionsByDateRange(
      filtered,
      filters.dateFrom,
      filters.dateTo,
    );
  } else if (filters.timePeriod !== "alltime") {
    const dateRange = getDateRangeFromTimePeriod(filters.timePeriod);
    if (dateRange) {
      filtered = filterTransactionsByDateRange(
        filtered,
        dateRange.start,
        dateRange.end,
      );
    }
  }

  // Apply category filter
  if (filters.categoryId) {
    filtered = filterTransactionsByCategory(filtered, filters.categoryId);
  }

  return filtered;
}

/**
 * Convert time period to date range
 */
export function getDateRangeFromTimePeriod(
  timePeriod: TimePeriod,
): { start: string; end: string } | null {
  const now = new Date();
  const end = now.toISOString().split("T")[0];

  switch (timePeriod) {
    case "last7days": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { start: start.toISOString().split("T")[0], end };
    }
    case "last30days": {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { start: start.toISOString().split("T")[0], end };
    }
    case "last90days": {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return { start: start.toISOString().split("T")[0], end };
    }
    case "alltime":
    case "custom":
      return null;
    default:
      return null;
  }
}

/**
 * Get unique categories from transactions
 */
export function getUniqueCategoriesFromTransactions(
  transactions: Transaction[],
): string[] {
  const categoryIds = new Set<string>();

  transactions.forEach((transaction) => {
    if (transaction.category_id) {
      categoryIds.add(transaction.category_id);
    }
  });

  return Array.from(categoryIds);
}

/**
 * Determine time granularity based on date range
 */
export function getTimeGranularity(
  startDate: string,
  endDate: string,
): "daily" | "weekly" | "monthly" {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const daysDiff = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysDiff < 30) {
    return "daily";
  } else if (daysDiff <= 90) {
    return "weekly";
  } else {
    return "monthly";
  }
}

/**
 * Format account type for display
 */
export function formatAccountType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
